"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { DashboardGrid, DashboardGridItem } from "./dashboard-grid";
import { WidgetSkeleton } from "./widget-card";

// A plausible dashboard: a full header strip and four cards. It IS a guess —
// the real shape isn't known until the catalog says which widgets this user has
// — but it's a guess in the app's own chrome, so the swap is a change of content
// rather than a change of layout.
const TILES = 4;
const CARDS = 4;

/**
 * Shown while the CATALOG is in flight, and only then.
 *
 * Cards fetch their own data, so this is followed by each card's own skeleton —
 * two loading states back to back. That is a real cost and worth being honest
 * about; it was briefly avoided by having the catalog carry the fast widgets'
 * payloads, at the price of every card's data waiting on the slowest DB read in
 * the response.
 *
 * What keeps the double-skeleton rare is `useDashboard` restoring the last
 * catalog from localStorage: a returning user renders their real layout on the
 * first paint and never reaches this component, so the cards' own skeletons are
 * the only loading state they see. This is the genuinely-first-visit case, where
 * the alternative is a blank page.
 */
export function DashboardSkeleton() {
  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: TILES }).map((_, index) => (
          <Card
            key={index}
            size="sm"
            className="h-full rounded-card border border-border bg-surface-main shadow-card-soft ring-0"
          >
            <CardContent className="flex flex-col gap-2">
              <Skeleton className="h-3 w-16" />
              {/* Same height as a real count tile's figure — see WidgetCard. */}
              <Skeleton className="h-7 w-24 lg:h-9" />
            </CardContent>
          </Card>
        ))}
      </div>

      <DashboardGrid>
        {Array.from({ length: CARDS }).map((_, index) => (
          <DashboardGridItem key={index} size="md">
            {/* Same shell as WidgetCard: band, hairline, body. Matching the real
                chrome is the point — only the contents resolve in. */}
            <div className="flex h-full flex-col overflow-hidden rounded-card border border-border bg-surface-main shadow-card-soft">
              <div className="flex items-center border-b border-border bg-folder-default px-5 py-3 dark:bg-folder-opened">
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex flex-1 flex-col px-5 pt-4 pb-4">
                <WidgetSkeleton rows={3} />
              </div>
            </div>
          </DashboardGridItem>
        ))}
      </DashboardGrid>
    </>
  );
}
